import { useState, useEffect } from 'react';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction
} from '@/components/ui/alert-dialog';
import { fetchInstagramPosts, PublicInstagramPost } from '@/lib/instagram-post-service';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle2 } from 'lucide-react';

interface ProfileImageSelectorProps {
    open: boolean;
    username: string;
    onClose: () => void;
    onSelect: (imageUrl: string) => void;
}

export default function ProfileImageSelector({
    open,
    username,
    onClose,
    onSelect
}: ProfileImageSelectorProps) {
    const [posts, setPosts] = useState<PublicInstagramPost[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setLoading(true);
            setError(null);
            setSelectedImage(null);

            fetchInstagramPosts(username)
                .then((fetchedPosts) => {
                    setPosts(fetchedPosts);
                    if (fetchedPosts.length === 0) {
                        setError('이 계정에 사용 가능한 게시물이 없습니다.');
                    }
                })
                .catch((err) => {
                    console.error('게시물 로드 오류:', err);
                    setError('게시물을 불러오는 중 오류가 발생했습니다.');
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [username, open]);

    const handleImageSelect = (imageUrl: string) => {
        setSelectedImage(imageUrl);
    };

    const handleConfirm = () => {
        if (selectedImage) {
            onSelect(selectedImage);
            onClose();
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={onClose}>
            <AlertDialogContent className="max-w-4xl w-[95vw]">
                <AlertDialogHeader>
                    <AlertDialogTitle>프로필 사진 선택</AlertDialogTitle>
                    <AlertDialogDescription>
                        {username}의 인스타그램 피드에서 채팅에 사용할 프로필 사진을 선택하세요.
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="my-6">
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {[...Array(4)].map((_, i) => (
                                <Skeleton key={i} className="aspect-square rounded-md" />
                            ))}
                        </div>
                    ) : error ? (
                        <div className="p-8 text-center text-muted-foreground">{error}</div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-h-[65vh] overflow-y-auto p-2">
                            {posts.map((post) => (
                                <div
                                    key={post.id}
                                    className={`
                    relative aspect-square rounded-lg overflow-hidden cursor-pointer 
                    border-3 hover:opacity-90 transition-all shadow-md
                    ${selectedImage === post.imagekit_url ? 'border-primary ring-4 ring-primary/50' : 'border-transparent'}
                  `}
                                    onClick={() => handleImageSelect(post.imagekit_url)}
                                >
                                    <img
                                        src={post.imagekit_url}
                                        alt={post.caption || '인스타그램 게시물'}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                    {selectedImage === post.imagekit_url && (
                                        <div className="absolute top-3 right-3 bg-primary rounded-full p-1 shadow-lg">
                                            <CheckCircle2 className="w-8 h-8 text-white" />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <AlertDialogFooter>
                    <AlertDialogCancel>취소</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={handleConfirm}
                        disabled={!selectedImage || loading}
                    >
                        선택 완료
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
} 